import type { ReactElement } from 'react';
import { Check } from 'lucide-react';
import {
  type MCPRiskLevel,
  type MCPRiskUnavailableReason,
} from '@kirin/api-client/contracts/mcp';
import { MCP_RISK_UNAVAILABLE_COPY } from './mcpRiskCopy';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AgentmeshTagBadge } from '@/components/shared/agentmesh/AgentmeshTagBadge';

interface McpRiskBadgeProps {
  level: MCPRiskLevel;
  size?: 'sm' | 'lg';
  className?: string;
}

/** AgentMesh risk value as a coloured pill: dot + label (KIR-5108). */
export function McpRiskBadge({
  level,
  size = 'sm',
  className,
}: McpRiskBadgeProps): ReactElement {
  return (
    <AgentmeshTagBadge
      tag={level}
      size={size}
      className={className}
      dotTestId="mcp-risk-dot"
    />
  );
}

interface McpRiskUnavailableProps {
  reason: MCPRiskUnavailableReason | null | undefined;
}

/** The dash for a row with no AgentMesh value, explaining why on hover. */
export function McpRiskUnavailable({
  reason,
}: McpRiskUnavailableProps): ReactElement {
  const copy = MCP_RISK_UNAVAILABLE_COPY[reason ?? 'no-record'];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={copy}
          className="text-muted-foreground cursor-default"
          tabIndex={0}
        >
          —
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p>{copy}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface McpRiskFilterOptionProps {
  level: MCPRiskLevel;
  isSelected: boolean;
}

/** One option of the Risk column's multi-select filter. */
export function McpRiskFilterOption({
  level,
  isSelected,
}: McpRiskFilterOptionProps): ReactElement {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <McpRiskBadge level={level} />
    </div>
  );
}
